import { ActorRefFrom, assign, fromPromise, setup } from "xstate";

import TimeRange from "@/types/time-range";
import { Dimension, DimensionValue, DimensionValueSet } from "@/lib/dimensions";
import { TimeRangeDimension } from "@/lib/dimensions/time-range-dimension";
import { ConstantDimension } from "@/lib/dimensions/constant-dimension";

import { LayerConfig, layerStateMachine } from "./layer-state-machine";
import Layer from "./layer";

export type MapStateMachineRef = ActorRefFrom<typeof mapStateMachine>;

export type MapStateMachineContext = {
  layerMachineRefs: ActorRefFrom<typeof layerStateMachine>[];
  timeRange: TimeRange;
  dimensions: Dimension[];
  currentDimensionValues: DimensionValueSet;
  canLoop: boolean;
  currentBufferingAbortController?: AbortController;
};

export type MapStateMachineInput = {
  layerConfigs: LayerConfig[];
  timeRange: TimeRange;
};

export type MapStateMachineEvent =
  | { type: "LAYER_DISABLED" }
  | { type: "LAYER_STARTED_INITIALIZING" }
  | { type: "LAYER_COMPLETED_INITIALIZING" }
  | { type: "LAYER_COMPLETED_LOADING" }
  | { type: "LAYER_REQUIRES_ADDITONAL_DATA" }
  | { type: "SET_DIMENSION_VALUE"; dimensionId: string; value: DimensionValue }
  | { type: "SET_TIME_RANGE"; timeRange: TimeRange }
  | { type: "REFRESH" }
  | { type: "TOGGLE_PLAYING" }
  | { type: "TOGGLE_PLAY_IN_LOOP" }
  | { type: "RECALCULATE_DIMENSIONS" };

export const mapStateMachine = setup({
  types: {
    context: {} as MapStateMachineContext,
    input: {} as MapStateMachineInput,
    events: {} as MapStateMachineEvent,
  },
  actions: {
    recalculateDimensions: assign(({ context }) => {
      const dimensions = context.layerMachineRefs
        .map((ref) => ref.getSnapshot())
        .filter((snapshot) => snapshot.matches({ enabled: "ready" }))
        .flatMap((snapshot) => snapshot.context.layer.dimensions)
        .filter((it) => !!it) // filter out undefined
        .filter((it) => !(it instanceof ConstantDimension)); // We can't merge these, Layers should keep track of these themselves.

      const groupedDimensions = dimensions.reduce(
        (grouped, dimension) => {
          grouped[dimension.id] ||= [];
          grouped[dimension.id].push(dimension);
          return grouped;
        },
        {} as Record<string, Dimension[]>,
      );

      const mergedDimensions = Object.values(groupedDimensions).map((dimensionGroup) => {
        const [firstDimension, ...rest] = dimensionGroup;
        return rest.reduce((mergedDimension, dimension) => {
          return mergedDimension.merge(dimension);
        }, firstDimension);
      });
      const croppedMergedDimensions = cropTimeDimensions(
        mergedDimensions,
        context.timeRange,
      );
      const croppedOutDimensionIds = getCroppedOutDimensionIds(
        mergedDimensions,
        croppedMergedDimensions,
      );

      const newDimensionValues = {} as DimensionValueSet;
      croppedMergedDimensions.forEach((dimension) => {
        const current = context.currentDimensionValues[dimension.id];
        if (!current) {
          newDimensionValues[dimension.id] = dimension.default;
          return;
        }

        const oldDimension = context.dimensions.find((it) => it.id === dimension.id);
        const wasDefaultValue =
          oldDimension?.default?.queryStringValue === current.queryStringValue;
        if (oldDimension && wasDefaultValue) {
          newDimensionValues[dimension.id] = dimension.default;
          return;
        }

        const newValue = dimension.contains(current) ? dimension.snap(current) : undefined;
        newDimensionValues[dimension.id] = newValue ?? dimension.default;
      });

      return {
        dimensions: croppedMergedDimensions,
        currentDimensionValues: {
          ...omitDimensionValues(context.currentDimensionValues, croppedOutDimensionIds),
          ...newDimensionValues,
        },
      };
    }),

    nextTimeDimension: assign(({ context }) => {
      const timeDimension = context.dimensions.find(
        (dimension) => dimension instanceof TimeRangeDimension,
      );
      const currentTimeDimensionValue = timeDimension
        ? context.currentDimensionValues[timeDimension.id]
        : undefined;
      if (!timeDimension || !currentTimeDimensionValue) return context;

      const next = timeDimension.next(currentTimeDimensionValue);
      if (!next && context.canLoop)
        return {
          currentDimensionValues: {
            ...context.currentDimensionValues,
            time: timeDimension.start,
          },
        };

      if (!next) return context;

      return {
        currentDimensionValues: {
          ...context.currentDimensionValues,
          time: next,
        },
      };
    }),
  },
  actors: {
    preload: fromPromise<void, MapStateMachineContext>(async ({ input }) => {
      const signal = input.currentBufferingAbortController?.signal;
      const entries = getEnabledReadyLayerEntries(input.layerMachineRefs);
      const timeline = generateTimeline(input.dimensions, input.currentDimensionValues);
      const notLoaded = timeline.filter((dimensionValues) =>
        entries.some(
          ({ layer }) =>
            !layer.isLoaded(dimensionValues) && !layer.hasLoadError(dimensionValues),
        ),
      );
      const tasks = notLoaded.flatMap((dimensionValues) =>
        entries.map(({ ref, layer }) => ({
          ref,
          promise: layer.preload(dimensionValues, 0, signal),
        })),
      );
      const results = await Promise.allSettled(tasks.map((task) => task.promise));

      if (signal?.aborted) return;

      const taskResults = tasks.map((task, index) => ({
        ref: task.ref,
        result: results[index],
      }));

      for (const { reason } of collectPreloadFailures(taskResults)) {
        console.error("Map layer preload failed", reason);
      }
    }),
  },
  guards: {
    allEnabledLayersAreLoadedOrFailed: ({ context }) => {
      return isTimelineSettledForLayers(
        getEnabledReadyLayers(context.layerMachineRefs),
        generateTimeline(context.dimensions, context.currentDimensionValues),
      );
    },
    allEnabledLayersAreLoaded: ({ context }) => {
      const timeline = generateTimeline(context.dimensions, context.currentDimensionValues);
      const layers = getEnabledReadyLayers(context.layerMachineRefs);
      return isTimelineFullyLoaded(layers, timeline);
    },
    canLoadNextTimeDimensionValue: ({ context }) => {
      const timeDimension = context.dimensions.find(
        (dimension) => dimension instanceof TimeRangeDimension,
      );
      const currentTimeDimensionValue = timeDimension
        ? context.currentDimensionValues[timeDimension.id]
        : undefined;
      if (!timeDimension || !currentTimeDimensionValue) return false;

      const next = timeDimension.next(currentTimeDimensionValue);
      return !!next || (!next && context.canLoop);
    },
  },
}).createMachine({
  id: "map",
  initial: "initializing",
  states: {
    initializing: {
      on: {
        LAYER_COMPLETED_INITIALIZING: {
          target: "initializing",
          reenter: true,
        },
      },
      always: {
        actions: "recalculateDimensions",
        target: "loading",
        guard: ({ context }) => {
          return context.layerMachineRefs
            .map((ref) => ref.getSnapshot())
            .filter((it) => it.matches("enabled"))
            .every(
              (it) =>
                !it.matches({ enabled: "initializing" }) &&
                !it.matches({ enabled: { ready: "reinitializing" } }),
            );
        },
      },
    },
    loading: {
      on: {
        LAYER_COMPLETED_LOADING: {
          target: "loading",
          reenter: true,
        },
      },
      always: {
        guard: ({ context }) => {
          return context.layerMachineRefs
            .map((ref) => ref.getSnapshot())
            .filter((it) => it.matches({ enabled: "ready" }))
            .every((it) => it.matches({ enabled: { ready: "idle" } }));
        },
        target: "buffering",
      },
    },
    buffering: {
      entry: assign({
        currentBufferingAbortController: () => new AbortController(),
      }),
      on: {
        LAYER_STARTED_INITIALIZING: {
          target: "initializing",
          actions: ({ context }) =>
            context.currentBufferingAbortController?.abort(
              new DOMException("mapStateMachine LAYER_STARTED_INITIALIZING", "AbortError"),
            ),
        },
        LAYER_REQUIRES_ADDITONAL_DATA: {
          target: "loading",
          actions: ({ context }) =>
            context.currentBufferingAbortController?.abort(
              new DOMException(
                "mapStateMachine LAYER_REQUIRES_ADDITONAL_DATA",
                "AbortError",
              ),
            ),
        },
      },
      invoke: {
        src: "preload",
        input: ({ context }) => context,
        onDone: [
          {
            target: "ready.history",
            guard: "allEnabledLayersAreLoadedOrFailed",
          },
          {
            target: "buffering",
            reenter: true,
          },
        ],
        onError: [
          {
            guard: ({ event }) => isAbortError(event.error),
            target: "buffering",
            reenter: true,
          },
          {
            actions: ({ event }) =>
              console.error("Unexpected map preload actor error", event.error),
            target: "ready.history",
          },
        ],
      },
    },
    ready: {
      initial: "paused",
      on: {
        LAYER_REQUIRES_ADDITONAL_DATA: "loading",
      },
      states: {
        history: { type: "history" },
        paused: {
          on: {
            TOGGLE_PLAYING: "playing",
          },
        },
        playing: {
          on: {
            TOGGLE_PLAYING: "paused",
          },
          after: {
            1000: [
              {
                guard: "canLoadNextTimeDimensionValue",
                actions: "nextTimeDimension",
                target: "playing",
                reenter: true,
              },
              {
                target: "paused",
              },
            ],
          },
        },
      },
    },
  },
  on: {
    REFRESH: {
      target: ".initializing",
      actions: ({ context }) => {
        context.layerMachineRefs.forEach((ref) => {
          ref.send({
            type: "REFRESH",
          });
        });
      },
    },
    SET_TIME_RANGE: {
      actions: [
        assign({ timeRange: ({ event }) => event.timeRange }),
        ({ context, event }) => {
          context.layerMachineRefs.forEach((ref) => {
            ref.send({
              type: "SET_TIME_RANGE",
              timeRange: event.timeRange,
            });
          });
        },
      ],
    },
    SET_DIMENSION_VALUE: {
      actions: assign({
        currentDimensionValues: ({ context, event }) => {
          const dimension = context.dimensions.find(
            (dimension) => dimension.id == event.dimensionId,
          );
          if (!dimension?.contains(event.value)) return context.currentDimensionValues;

          return {
            ...context.currentDimensionValues,
            [event.dimensionId]: event.value,
          };
        },
      }),
    },
    TOGGLE_PLAY_IN_LOOP: {
      actions: assign({
        canLoop: ({ context }) => !context.canLoop,
      }),
    },
    LAYER_STARTED_INITIALIZING: ".initializing",
    LAYER_DISABLED: {
      actions: ["recalculateDimensions"],
    },
    RECALCULATE_DIMENSIONS: {
      actions: "recalculateDimensions",
    },
  },
  context: ({ input, spawn }) => {
    return {
      dimensions: [],
      timeRange: input.timeRange,
      currentDimensionValues: {},
      canLoop: true,
      layerMachineRefs: input.layerConfigs
        .map((layerConfig) => {
          return spawn<typeof layerStateMachine>(layerStateMachine, {
            systemId: `layer-${layerConfig.layer.id}`,
            input: {
              layerConfig,
              timeRange: input.timeRange,
            },
          });
        })
        .filter((layer) => !!layer),
    };
  },
});

function cropTimeDimensions(dimensions: Dimension[], timeRange: TimeRange) {
  return dimensions
    .map((dimension) => {
      if (!(dimension instanceof TimeRangeDimension)) return dimension;

      return dimension.slice(timeRange.timeStart, timeRange.timeEnd);
    })
    .filter((dimension): dimension is Dimension => !!dimension);
}

function getCroppedOutDimensionIds(
  dimensions: Dimension[],
  croppedDimensions: Dimension[],
) {
  const croppedDimensionIds = new Set(croppedDimensions.map((dimension) => dimension.id));

  return new Set(
    dimensions
      .filter((dimension) => !croppedDimensionIds.has(dimension.id))
      .map((dimension) => dimension.id),
  );
}

function omitDimensionValues(
  values: DimensionValueSet,
  dimensionIds: Set<string>,
): DimensionValueSet {
  return Object.fromEntries(
    Object.entries(values).filter(([dimensionId]) => !dimensionIds.has(dimensionId)),
  );
}

function generateTimeline(
  dimensions: Dimension[],
  currentDimensionValues: DimensionValueSet,
) {
  const timeRangeDimension = dimensions.find(
    (dimension) => dimension instanceof TimeRangeDimension,
  );

  if (!timeRangeDimension) return [currentDimensionValues];

  let currentIndex = timeRangeDimension.values.findIndex(
    (value) =>
      value.queryStringValue ===
      currentDimensionValues[timeRangeDimension.id]?.queryStringValue,
  );
  if (currentIndex === -1) currentIndex = 0;

  const sortedTimeRangeDimensionValues = [
    ...timeRangeDimension.values.slice(currentIndex),
    ...timeRangeDimension.values.slice(0, currentIndex),
  ];

  const sortedDimensionValues = sortedTimeRangeDimensionValues.map(
    (timeRangeDimensionValue) => ({
      ...currentDimensionValues,
      [timeRangeDimension.id]: timeRangeDimensionValue,
    }),
  );

  return sortedDimensionValues;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function isTimelineFullyLoaded(layers: Layer[], timeline: DimensionValueSet[]) {
  return layers.every((layer) => {
    return timeline.every((dimensionValues) => layer.isLoaded(dimensionValues));
  });
}

export function isTimelineSettledForLayers(layers: Layer[], timeline: DimensionValueSet[]) {
  return layers.every((layer) => {
    return timeline.every(
      (dimensionValues) =>
        layer.isLoaded(dimensionValues) || layer.hasLoadError(dimensionValues),
    );
  });
}

export type PreloadTaskResult<TRef> = {
  ref: TRef;
  result: PromiseSettledResult<unknown>;
};

export function collectPreloadFailures<TRef>(
  taskResults: PreloadTaskResult<TRef>[],
): { ref: TRef; reason: unknown }[] {
  const seenRefs = new Set<TRef>();
  const failures = [] as { ref: TRef; reason: unknown }[];

  for (const { ref, result } of taskResults) {
    if (result.status !== "rejected" || isAbortError(result.reason)) continue;
    if (seenRefs.has(ref)) continue;

    seenRefs.add(ref);
    failures.push({ ref, reason: result.reason });
  }

  return failures;
}

type EnabledReadyLayerEntry = {
  ref: ActorRefFrom<typeof layerStateMachine>;
  layer: Layer;
};

function getEnabledReadyLayerEntries(
  layerMachineRefs: ActorRefFrom<typeof layerStateMachine>[],
): EnabledReadyLayerEntry[] {
  return layerMachineRefs
    .map((ref) => ({ ref, snapshot: ref.getSnapshot() }))
    .filter(({ snapshot }) => snapshot.matches({ enabled: "ready" }))
    .map(({ ref, snapshot }) => ({ ref, layer: snapshot.context.layer }));
}

function getEnabledReadyLayers(layerMachineRefs: ActorRefFrom<typeof layerStateMachine>[]) {
  return getEnabledReadyLayerEntries(layerMachineRefs).map((entry) => entry.layer);
}

export default mapStateMachine;
